import { useEffect, useState } from "react";
import { api, API, fmtINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Building2, Mail, Send, Edit, Copy, Check, ExternalLink, RefreshCw, 
  Download, Phone, MessageSquare, User, Eye, AlertCircle, Clock, 
  CheckCircle2, FileText, ArrowUpRight
} from "lucide-react";

export function AdminBookings() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [status, setStatus] = useState("all");
  const [organizerSlug, setOrganizerSlug] = useState("all");
  const [organizers, setOrganizers] = useState([]);
  const [q, setQ] = useState("");

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (organizerSlug !== "all") params.set("organizer_slug", organizerSlug);
    if (q) params.set("q", q);
    api.get(`/admin/bookings?${params}`)
      .then((r) => setItems(r.data))
      .catch((err) => toast.error("Failed to load bookings: " + (err.response?.data?.detail || err.message)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isSuperAdmin) {
      api.get("/admin/organizers").then((r) => setOrganizers(r.data)).catch(() => {});
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    load();
  }, [status, organizerSlug]);

  const handleExportCsv = async () => {
    try {
      setExporting(true);
      toast.info("Generating CSV export...");
      const params = new URLSearchParams();
      if (status !== "all") params.set("status", status);
      if (organizerSlug !== "all") params.set("organizer_slug", organizerSlug);
      if (q) params.set("q", q);

      const res = await api.get(`/admin/exports/bookings.csv?${params}`, {
        responseType: "blob",
      });

      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `cgc_bookings_${dateStr}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV export downloaded successfully!");
    } catch (err) {
      toast.error("Failed to export CSV: " + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Quick filter tabs
  const filterTabs = [
    { id: "all", label: "All Bookings" },
    { id: "paid", label: "Paid" },
    { id: "missed_sales", label: "Missed Sales (Abandoned)", badge: "Leads" },
    { id: "pending", label: "Pending" },
    { id: "expired", label: "Expired" },
    { id: "refunded", label: "Refunded" },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-3xl font-bold">Bookings & Attendees</h1>
          <p className="text-xs text-stone-500 mt-0.5">
            Track confirmed registrations, manage attendee emails, and follow up on abandoned checkouts.
          </p>
        </div>
        {user?.role === "organizer" && (
          <span className="text-xs px-2.5 py-1 rounded-full bg-orange-100 text-orange-800 dark:bg-orange-950/70 dark:text-orange-300 font-semibold border border-orange-200">
            Partner Portal: {user?.name || user?.organizer_slug}
          </span>
        )}
      </div>

      {/* Quick Filter Pills */}
      <div className="flex items-center gap-1.5 mt-5 overflow-x-auto pb-1 border-b border-border text-xs">
        {filterTabs.map((tab) => {
          const active = status === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setStatus(tab.id)}
              className={`px-3 py-1.5 rounded-full font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                active
                  ? "bg-orange-600 text-white shadow-sm"
                  : "bg-secondary/60 text-stone-600 dark:text-stone-300 hover:bg-secondary"
              }`}
            >
              <span>{tab.label}</span>
              {tab.badge && (
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  active ? "bg-white/20 text-white" : "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300"
                }`}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search & Actions Bar */}
      <div className="flex flex-wrap gap-2 mt-4 items-center">
        <Input
          data-testid="bookings-search"
          placeholder="Search name, phone, email, ref..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") load(); }}
          className="max-w-xs"
        />
        <Button variant="outline" onClick={load} data-testid="bookings-search-btn">
          Search
        </Button>
        
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="bookings-status" className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="missed_sales">Missed Sales (Abandoned)</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="free_confirmed">Free confirmed</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
            <SelectItem value="partially_refunded">Partially refunded</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>

        {isSuperAdmin && (
          <div className="flex items-center gap-1.5 bg-card border border-border px-2.5 py-1 rounded-md text-xs h-9">
            <Building2 size={13} className="text-orange-600" />
            <select
              className="bg-transparent text-xs font-medium border-0 focus:ring-0 text-stone-900 dark:text-stone-100 cursor-pointer"
              value={organizerSlug}
              onChange={(e) => setOrganizerSlug(e.target.value)}
            >
              <option value="all">All Organizers</option>
              <option value="cgc">Common Ground (CGC Direct)</option>
              {organizers.map((o) => (
                <option key={o.id || o.slug} value={o.slug}>{o.name} ({o.slug})</option>
              ))}
            </select>
          </div>
        )}

        <Button
          variant="outline"
          onClick={handleExportCsv}
          disabled={exporting}
          data-testid="bookings-export"
          className="ml-auto gap-1.5 text-xs"
        >
          {exporting ? <RefreshCw size={13} className="animate-spin" /> : <Download size={13} />}
          <span>{exporting ? "Exporting..." : "Export CSV"}</span>
        </Button>
      </div>

      {/* Bookings Table */}
      <div className="mt-4 cgc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60">
            <tr>
              {["Reference", ...(isSuperAdmin ? ["Partner"] : []), "Event", "Purchaser & Contact", "Total", "Status", "Date", "Actions"].map((h) => (
                <th key={h} className="text-left p-3 font-medium text-xs">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const isMissedSale = (b.status === "pending" || b.status === "expired") && (b.purchaser_phone || b.purchaser_email);
              const cleanPhone = (b.purchaser_phone || "").replace(/[^0-9]/g, "");

              return (
                <tr key={b.id} className="border-t border-border hover:bg-stone-50/50 dark:hover:bg-stone-900/50 transition-colors">
                  {/* Reference */}
                  <td className="p-3">
                    <div className="font-mono text-xs font-semibold text-stone-800 dark:text-stone-200">
                      {b.reference}
                    </div>
                    {isMissedSale && (
                      <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-200 dark:border-amber-900">
                        Missed Sale
                      </span>
                    )}
                  </td>

                  {/* Partner Column */}
                  {isSuperAdmin && (
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded text-[11px] font-mono ${
                        b.organizer_slug && b.organizer_slug !== "cgc"
                          ? "bg-orange-100 text-orange-800 dark:bg-orange-950/70 dark:text-orange-300 border border-orange-200"
                          : "bg-stone-100 text-stone-600 dark:bg-stone-800 dark:text-stone-400"
                      }`}>
                        {b.organizer_slug && b.organizer_slug !== "cgc" ? b.organizer_slug : "cgc"}
                      </span>
                    </td>
                  )}

                  {/* Event */}
                  <td className="p-3 font-medium max-w-[200px]">
                    <div className="truncate" title={b.event_title}>{b.event_title}</div>
                    <div className="text-[11px] text-stone-400 mt-0.5">
                      {b.attendees?.length || 1} pass{(b.attendees?.length || 1) > 1 ? "es" : ""}
                    </div>
                  </td>

                  {/* Purchaser & Contact */}
                  <td className="p-3">
                    <div className="font-medium text-stone-900 dark:text-stone-100">
                      {b.purchaser_name || "Guest Attendee"}
                    </div>
                    
                    {/* Email with quick edit */}
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Mail size={11} className="text-stone-400 shrink-0" />
                      <span className="text-xs text-stone-500 font-mono truncate max-w-[170px]">
                        {b.purchaser_email || "No email"}
                      </span>
                      <EditEmailAndResendDialog
                        b={b}
                        onDone={load}
                        trigger={
                          <button
                            type="button"
                            title="Edit email or resend ticket"
                            className="text-stone-400 hover:text-orange-600 transition-colors p-0.5"
                          >
                            <Edit size={11} />
                          </button>
                        }
                      />
                    </div>

                    {/* Phone & Quick Contact Buttons */}
                    {b.purchaser_phone && (
                      <div className="flex items-center gap-1.5 mt-1 text-xs">
                        <Phone size={11} className="text-stone-400 shrink-0" />
                        <span className="font-mono text-stone-600 dark:text-stone-300 font-medium text-[11px]">
                          {b.purchaser_phone}
                        </span>
                        
                        {/* WhatsApp One-Click Action */}
                        {cleanPhone.length >= 10 && (
                          <a
                            href={`https://wa.me/${cleanPhone.length === 10 ? '91' + cleanPhone : cleanPhone}?text=${encodeURIComponent(
                              `Hi ${b.purchaser_name || 'there'}, Common Ground Collective India team here regarding your pass for ${b.event_title}.`
                            )}`}
                            target="_blank"
                            rel="noreferrer"
                            title="Message on WhatsApp"
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-950/60 dark:text-green-300 border border-green-200"
                          >
                            <MessageSquare size={10} />
                            <span>WA</span>
                          </a>
                        )}

                        {/* Direct Call */}
                        <a
                          href={`tel:${b.purchaser_phone}`}
                          title="Call purchaser"
                          className="text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 p-0.5"
                        >
                          <Phone size={10} />
                        </a>
                      </div>
                    )}
                  </td>

                  {/* Total */}
                  <td className="p-3 font-mono text-sm">{fmtINR(b.total_paise)}</td>

                  {/* Status */}
                  <td className="p-3"><StatusBadge s={b.status} /></td>

                  {/* Created Date */}
                  <td className="p-3 text-xs text-stone-500 whitespace-nowrap">{fmtDate(b.created_at)}</td>

                  {/* Actions Column */}
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <BookingDetailsDialog b={b} onDone={load} />
                      <EditEmailAndResendDialog b={b} onDone={load} />
                      <ManualActivateDialog b={b} onDone={load} />
                    <RefundDialog b={b} onDone={load} />
                    </div>
                  </td>
                </tr>
              );
            })}

            {items.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 8 : 7} className="text-center p-12 text-stone-500">
                  {loading ? (
                    <div className="flex items-center justify-center gap-2">
                      <RefreshCw size={15} className="animate-spin text-orange-600" />
                      <span>Loading bookings...</span>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium text-stone-700 dark:text-stone-300">No bookings found</p>
                      <p className="text-xs text-stone-400 mt-1">Try clearing your search query or changing status filters.</p>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusBadge({ s }) {
  const colors = {
    paid: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300 border-green-200",
    free_confirmed: "bg-green-100 text-green-900 dark:bg-green-950 dark:text-green-300 border-green-200",
    pending: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300 border-amber-200",
    expired: "bg-stone-200 text-stone-700 dark:bg-stone-800 dark:text-stone-400 border-stone-300",
    failed: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-300 border-red-200",
    refunded: "bg-stone-200 text-stone-800 dark:bg-stone-800 dark:text-stone-300 border-stone-300",
    partially_refunded: "bg-stone-200 text-stone-800 dark:bg-stone-800 dark:text-stone-300 border-stone-300",
    cancelled: "bg-stone-200 text-stone-800 dark:bg-stone-800 dark:text-stone-300 border-stone-300",
  }[s] || "bg-stone-100 text-stone-800";
  return (
    <span className={`px-2 py-0.5 rounded text-[11px] font-mono font-medium border uppercase tracking-wider ${colors}`}>
      {s?.replace("_", " ")}
    </span>
  );
}

// ---------------- Full Booking Details & Contact Dossier Modal ----------------
function BookingDetailsDialog({ b, onDone }) {
  const [open, setOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  const cleanPhone = (b.purchaser_phone || "").replace(/[^0-9]/g, "");
  const bookingPublicUrl = `${window.location.origin}/booking/${b.reference}`;

  const copyLink = () => {
    navigator.clipboard.writeText(bookingPublicUrl);
    setCopiedLink(true);
    toast.success("Public booking link copied!");
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const copyPhone = () => {
    if (b.purchaser_phone) {
      navigator.clipboard.writeText(b.purchaser_phone);
      setCopiedPhone(true);
      toast.success("Phone number copied!");
      setTimeout(() => setCopiedPhone(false), 2000);
    }
  };

  const isMissedSale = (b.status === "pending" || b.status === "expired") && (b.purchaser_phone || b.purchaser_email);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="h-8 gap-1 text-xs border-stone-300 dark:border-stone-700 hover:border-orange-500 hover:text-orange-600"
          title="View full attendee dossier & contact info"
        >
          <Eye size={13} />
          <span>View</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 pr-6">
            <DialogTitle className="text-lg font-bold flex items-center gap-2">
              <span>Booking</span>
              <span className="font-mono text-orange-600">{b.reference}</span>
            </DialogTitle>
            <StatusBadge s={b.status} />
          </div>
          <p className="text-xs text-stone-500 font-medium">{b.event_title}</p>
        </DialogHeader>

        <div className="space-y-4 pt-2 text-sm">
          {/* Missed Sale / Lead Follow-up Banner */}
          {isMissedSale && (
            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={16} />
                <div>
                  <h4 className="text-xs font-bold text-amber-900 dark:text-amber-200">
                    Missed Sale Follow-Up Opportunity
                  </h4>
                  <p className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                    This user filled their contact info during checkout but did not complete payment ({b.status}). Reach out directly to recover this pass!
                  </p>
                  {cleanPhone.length >= 10 && (
                    <div className="mt-2.5 flex items-center gap-2">
                      <a
                        href={`https://wa.me/${cleanPhone.length === 10 ? '91' + cleanPhone : cleanPhone}?text=${encodeURIComponent(
                          `Hi ${b.purchaser_name || 'there'}, Common Ground Collective India team here! We noticed you started registering for ${b.event_title}. Can we help you complete your booking?`
                        )}`}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-brand inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md"
                      >
                        <MessageSquare size={13} />
                        <span>Follow Up via WhatsApp</span>
                      </a>
                      <a
                        href={`tel:${b.purchaser_phone}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-stone-100 hover:bg-stone-200 dark:bg-stone-800 text-stone-800 dark:text-stone-200"
                      >
                        <Phone size={13} />
                        <span>Call</span>
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Primary Contact Dossier Card */}
          <div className="bg-stone-50 dark:bg-stone-900 border border-border rounded-lg p-3.5 space-y-3">
            <div className="flex items-center justify-between border-b border-border pb-2">
              <span className="text-xs font-bold text-stone-700 dark:text-stone-300 flex items-center gap-1.5">
                <User size={13} className="text-orange-600" />
                <span>Purchaser Contact Details</span>
              </span>
              <span className="text-[11px] text-stone-400">
                Created: {fmtDate(b.created_at)}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-stone-500 block text-[11px]">Full Name</span>
                <span className="font-semibold text-stone-900 dark:text-stone-100 text-sm">
                  {b.purchaser_name || "Not provided"}
                </span>
              </div>

              <div>
                <span className="text-stone-500 block text-[11px]">Phone Number</span>
                {b.purchaser_phone ? (
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="font-mono font-bold text-stone-900 dark:text-stone-100">
                      {b.purchaser_phone}
                    </span>
                    <button
                      type="button"
                      onClick={copyPhone}
                      className="text-stone-400 hover:text-stone-800 p-0.5"
                      title="Copy phone"
                    >
                      {copiedPhone ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                    </button>
                    {cleanPhone.length >= 10 && (
                      <a
                        href={`https://wa.me/${cleanPhone.length === 10 ? '91' + cleanPhone : cleanPhone}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Chat on WhatsApp"
                        className="text-green-600 hover:text-green-700 p-0.5"
                      >
                        <MessageSquare size={12} />
                      </a>
                    )}
                  </div>
                ) : (
                  <span className="text-stone-400 italic">No phone entered</span>
                )}
              </div>

              <div className="sm:col-span-2">
                <span className="text-stone-500 block text-[11px]">Email Address</span>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="font-mono text-stone-900 dark:text-stone-100 font-semibold">
                    {b.purchaser_email || "No email on record"}
                  </span>
                  {b.purchaser_email && (
                    <a
                      href={`mailto:${b.purchaser_email}`}
                      className="text-orange-600 hover:text-orange-700 p-0.5"
                      title="Send email"
                    >
                      <Mail size={12} />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Attendees List & Answers */}
          <div className="space-y-2">
            <h4 className="text-xs font-bold text-stone-700 dark:text-stone-300 uppercase tracking-wider">
              Attendees & Passes ({b.attendees?.length || 0})
            </h4>
            <div className="space-y-2">
              {b.attendees?.map((att, idx) => (
                <div
                  key={att.id || idx}
                  className="p-3 rounded-lg border border-border bg-card text-xs space-y-1.5"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-semibold text-stone-900 dark:text-stone-100">
                        {att.name || "Attendee " + (idx + 1)}
                      </span>
                      {att.tier_name && (
                        <span className="ml-2 px-2 py-0.5 rounded text-[10px] bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 font-mono">
                          {att.tier_name}
                        </span>
                      )}
                    </div>
                    <span className="font-mono text-[11px] text-stone-400">
                      Ticket ID: {att.ticket_id || att.id}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-stone-500">
                    {att.email && (
                      <span className="flex items-center gap-1 font-mono">
                        <Mail size={11} /> {att.email}
                      </span>
                    )}
                    {att.phone && (
                      <span className="flex items-center gap-1 font-mono">
                        <Phone size={11} /> {att.phone}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      {att.checked_in ? (
                        <span className="text-green-600 font-medium flex items-center gap-0.5">
                          <CheckCircle2 size={11} /> Checked In
                        </span>
                      ) : (
                        <span className="text-stone-400 flex items-center gap-0.5">
                          <Clock size={11} /> Not Checked In
                        </span>
                      )}
                    </span>
                  </div>

                  {/* Custom Answers */}
                  {att.answers && Object.keys(att.answers).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px]">
                      {Object.entries(att.answers).map(([q, ans]) => (
                        <div key={q} className="bg-secondary/40 p-1.5 rounded">
                          <span className="text-stone-500 block text-[10px]">{q}</span>
                          <span className="font-medium text-stone-800 dark:text-stone-200">
                            {typeof ans === "boolean" ? (ans ? "Yes" : "No") : String(ans)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Billing & Tax (if provided) */}
          {b.billing && (b.billing.gstin || b.billing.address || b.billing.city) && (
            <div className="bg-stone-50 dark:bg-stone-900 border border-border rounded-lg p-3 text-xs space-y-1.5">
              <span className="font-bold text-stone-700 dark:text-stone-300 block text-[11px] uppercase tracking-wider">
                Billing & GST Details
              </span>
              <div className="grid grid-cols-2 gap-2 text-stone-600 dark:text-stone-400">
                {b.billing.name && <div><b>Company/Name:</b> {b.billing.name}</div>}
                {b.billing.gstin && <div><b>GSTIN:</b> <span className="font-mono">{b.billing.gstin}</span></div>}
                {b.billing.address && <div className="col-span-2"><b>Address:</b> {b.billing.address}</div>}
                {(b.billing.city || b.billing.state) && (
                  <div><b>Location:</b> {[b.billing.city, b.billing.state, b.billing.pincode].filter(Boolean).join(", ")}</div>
                )}
              </div>
            </div>
          )}

          {/* Financial Breakdown */}
          <div className="border border-border rounded-lg p-3 bg-card text-xs space-y-1.5">
            <div className="flex justify-between font-semibold">
              <span>Total Paid / Payable</span>
              <span className="font-mono text-sm">{fmtINR(b.total_paise)}</span>
            </div>
            <div className="flex justify-between text-stone-500 text-[11px]">
              <span>Payment Method</span>
              <span className="font-mono uppercase">{b.payment_method || "razorpay"}</span>
            </div>
            {b.razorpay_payment_id && (
              <div className="flex justify-between text-stone-500 text-[11px]">
                <span>Razorpay Payment ID</span>
                <span className="font-mono">{b.razorpay_payment_id}</span>
              </div>
            )}
            {b.notes && (
              <div className="pt-1 text-[11px] text-stone-500 italic">
                Note: {b.notes}
              </div>
            )}
          </div>

          {/* Modal Footer Actions */}
          <div className="pt-2 flex items-center justify-between border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={copyLink}
              className="gap-1.5 text-xs h-8"
            >
              {copiedLink ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
              <span>{copiedLink ? "Link Copied!" : "Copy Ticket Link"}</span>
            </Button>

            <a
              href={bookingPublicUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700 font-medium"
            >
              <span>Open Public Page</span>
              <ArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Edit Email & Resend Modal ----------------
function EditEmailAndResendDialog({ b, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState(b.purchaser_email || "");
  const [updateAttendees, setUpdateAttendees] = useState(true);
  const [resendNow, setResendNow] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resendingOnly, setResendingOnly] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setNewEmail(b.purchaser_email || "");
      setUpdateAttendees(true);
      setResendNow(true);
    }
  }, [open, b.purchaser_email]);

  const bookingPublicUrl = `${window.location.origin}/booking/${b.reference}`;

  const copyBookingLink = () => {
    navigator.clipboard.writeText(bookingPublicUrl);
    setCopied(true);
    toast.success("Public booking link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUpdateEmail = async (e) => {
    e?.preventDefault();
    if (!newEmail || !newEmail.includes("@") || !newEmail.includes(".")) {
      return toast.error("Please enter a valid email address");
    }

    try {
      setSaving(true);
      const res = await api.put(`/admin/bookings/${b.id}/email`, {
        new_email: newEmail.trim(),
        update_attendees: updateAttendees,
        resend: resendNow,
      });

      if (res.data?.email_result?.status === "sent") {
        toast.success(`Email updated & confirmation sent to ${newEmail.trim()}`);
      } else if (res.data?.email_result?.status === "skipped") {
        toast.success(`Email updated in database (SMTP dispatch skipped: ${res.data?.email_result?.reason || ""})`);
      } else if (res.data?.email_result?.status === "failed") {
        toast.warning(`Email updated in database, but SMTP dispatch failed: ${res.data?.email_result?.error || "Check mail settings"}`);
      } else {
        toast.success(res.data?.message || "Booking email updated successfully!");
      }

      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update email");
    } finally {
      setSaving(false);
    }
  };

  const handleResendOnly = async () => {
    const target = (b.purchaser_email || "").trim();
    if (!target) {
      return toast.error("No email on this booking. Please edit and provide an email first.");
    }

    try {
      setResendingOnly(true);
      const res = await api.post(`/admin/bookings/${b.id}/resend-email`, {
        to_email: target,
      });

      if (res.data?.email_result?.status === "sent") {
        toast.success(`Confirmation email resent to ${target}!`);
      } else if (res.data?.email_result?.status === "skipped") {
        toast.info(`Email dispatch skipped (${res.data?.email_result?.reason || "SMTP inactive"})`);
      } else if (res.data?.email_result?.status === "failed") {
        toast.error(`Email delivery failed: ${res.data?.email_result?.error || "SMTP error"}`);
      } else {
        toast.success(`Confirmation email resent to ${target}!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to resend email");
    } finally {
      setResendingOnly(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1 text-xs border-stone-300 dark:border-stone-700 hover:border-orange-500 hover:text-orange-600"
            data-testid={`email-btn-${b.id}`}
          >
            <Mail size={13} />
            <span>Email</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail className="text-orange-600" size={18} />
            <span>Manage Booking Email & Tickets</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2 text-sm">
          {/* Booking Summary */}
          <div className="bg-stone-50 dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-lg p-3 space-y-1">
            <div className="flex justify-between items-center text-xs text-stone-500">
              <span>Ref: <b className="font-mono text-stone-800 dark:text-stone-200">{b.reference}</b></span>
              <StatusBadge s={b.status} />
            </div>
            <div className="font-semibold text-stone-900 dark:text-stone-100">{b.event_title}</div>
            <div className="text-xs text-stone-600 dark:text-stone-400">
              Purchaser: <b>{b.purchaser_name}</b> ({b.purchaser_email || "No email"})
            </div>
            {b.attendees && b.attendees.length > 0 && (
              <div className="text-[11px] text-stone-500 pt-1 border-t border-stone-200 dark:border-stone-800">
                Attendees ({b.attendees.length}): {b.attendees.map((a) => a.name).join(", ")}
              </div>
            )}
          </div>

          {/* Quick Resend */}
          {b.purchaser_email && (
            <div className="flex items-center justify-between p-2.5 rounded-lg border border-orange-200 bg-orange-50/50 dark:border-orange-950 dark:bg-orange-950/20">
              <div className="text-xs">
                <span className="text-stone-600 dark:text-stone-400">Current email:</span>{" "}
                <b className="font-mono text-stone-900 dark:text-stone-100">{b.purchaser_email}</b>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={handleResendOnly}
                disabled={resendingOnly}
                className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:text-orange-300 gap-1"
                data-testid={`resend-direct-${b.id}`}
              >
                {resendingOnly ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />}
                <span>{resendingOnly ? "Sending..." : "Resend Now"}</span>
              </Button>
            </div>
          )}

          {/* Edit / Correct Email Form */}
          <form onSubmit={handleUpdateEmail} className="space-y-3 pt-1 border-t border-border">
            <div>
              <Label className="text-xs font-semibold text-stone-700 dark:text-stone-300">
                Correct / New Email Address
              </Label>
              <Input
                type="email"
                placeholder="e.g. attendee@example.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                className="mt-1 font-mono text-sm"
                required
                data-testid="input-new-booking-email"
              />
              <p className="text-[11px] text-stone-500 mt-1">
                Fix typos if user entered a wrong email during registration.
              </p>
            </div>

            <div className="space-y-2 pt-1">
              <label className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300 cursor-pointer">
                <Checkbox
                  checked={updateAttendees}
                  onCheckedChange={(checked) => setUpdateAttendees(!!checked)}
                />
                <span>Also update email on all attendee tickets for this booking</span>
              </label>

              <label className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300 cursor-pointer">
                <Checkbox
                  checked={resendNow}
                  onCheckedChange={(checked) => setResendNow(!!checked)}
                />
                <span>Immediately send confirmation email to the new address</span>
              </label>
            </div>

            <div className="pt-2 flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={copyBookingLink}
                className="text-xs text-stone-600 gap-1 h-8"
              >
                {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
                <span>{copied ? "Copied Link" : "Copy Ticket Link"}</span>
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="text-xs h-8"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  size="sm"
                  disabled={saving}
                  className="btn-brand text-xs h-8 gap-1"
                  data-testid="btn-save-booking-email"
                >
                  {saving ? <RefreshCw size={12} className="animate-spin" /> : <Check size={12} />}
                  <span>{saving ? "Saving..." : resendNow ? "Update & Resend" : "Update Email"}</span>
                </Button>
              </div>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Process Refund Modal ----------------
function RefundDialog({ b, onDone }) {
  const [amt, setAmt] = useState("");
  const [reason, setReason] = useState("");
  const remaining = b.total_paise - (b.refunded_paise || 0);
  if (b.status !== "paid" && b.status !== "partially_refunded") return null;
  const submit = async () => {
    try {
      const rupees = parseFloat(amt);
      if (isNaN(rupees) || rupees <= 0) return toast.error("Enter a valid refund amount in ₹");
      const amtPaise = Math.round(rupees * 100);
      if (amtPaise > remaining) return toast.error(`Amount exceeds remaining ${fmtINR(remaining)}`);
      await api.post("/admin/refund", { booking_id: b.id, amount_paise: amtPaise, reason });
      toast.success("Refund recorded");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`refund-${b.id}`}>
          Refund
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Refund {b.reference}</DialogTitle></DialogHeader>
        <div className="text-sm">Remaining refundable: <b>{fmtINR(remaining)}</b></div>
        <div>
          <Label className="text-xs">Refund amount (₹)</Label>
          <Input
            type="number"
            min="0"
            step="any"
            placeholder={`Amount in ₹ (Max: ₹${(remaining/100).toFixed(2)})`}
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
          />
        </div>
        <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <Button onClick={submit} className="btn-brand">Process refund</Button>
      </DialogContent>
    </Dialog>
  );
}


// ---------------- Manual Activate / Mark Paid Modal ----------------
function ManualActivateDialog({ b, onDone }) {
  const [open, setOpen] = useState(false);
  const [payId, setPayId] = useState(b.razorpay_payment_id || "");
  const [note, setNote] = useState("Payment verified on Razorpay");
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleActivate = async (e) => {
    e?.preventDefault();
    try {
      setLoading(true);
      const res = await api.post(`/admin/bookings/${b.id}/activate-paid`, {
        razorpay_payment_id: payId.trim() || undefined,
        note: note.trim() || undefined,
        send_email: sendEmail,
      });

      toast.success(res.data?.message || "Pass activated and marked paid successfully!");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to activate booking");
    } finally {
      setLoading(false);
    }
  };

  if (b.status === "paid") return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="h-8 gap-1 text-xs bg-green-600 hover:bg-green-700 text-white font-semibold"
          title="Manually confirm payment and issue tickets"
        >
          <CheckCircle2 size={13} />
          <span>Activate Paid</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg text-green-700 dark:text-green-400">
            <CheckCircle2 size={20} />
            <span>Confirm Payment & Activate Passes</span>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleActivate} className="space-y-3.5 text-sm pt-2">
          <div className="bg-stone-50 dark:bg-stone-900 border border-border rounded-lg p-3 space-y-1">
            <div className="flex justify-between items-center text-xs text-stone-500">
              <span>Ref: <b className="font-mono text-stone-800 dark:text-stone-200">{b.reference}</b></span>
              <StatusBadge s={b.status} />
            </div>
            <div className="font-semibold text-stone-900 dark:text-stone-100">{b.event_title}</div>
            <div className="text-xs text-stone-600 dark:text-stone-400">
              Purchaser: <b>{b.purchaser_name}</b> ({b.purchaser_email})
            </div>
            <div className="text-xs font-mono font-bold text-stone-900 dark:text-stone-100 pt-1">
              Amount: {fmtINR(b.total_paise)} ({b.attendees?.length || 1} passes)
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold text-stone-700 dark:text-stone-300">
              Razorpay Payment ID (from Razorpay email/dashboard)
            </Label>
            <Input
              placeholder="e.g. pay_Tn3Gd5S7XVm82J"
              value={payId}
              onChange={(e) => setPayId(e.target.value)}
              className="mt-1 font-mono text-xs"
            />
            <p className="text-[11px] text-stone-500 mt-1">
              Linking this ID updates the receipt and marks the payment captured in your financial exports.
            </p>
          </div>

          <div>
            <Label className="text-xs font-semibold text-stone-700 dark:text-stone-300">
              Admin Note / Reason
            </Label>
            <Input
              placeholder="e.g. Paid on UPI after session expired"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="mt-1 text-xs"
            />
          </div>

          <label className="flex items-center gap-2 text-xs text-stone-700 dark:text-stone-300 cursor-pointer pt-1">
            <Checkbox
              checked={sendEmail}
              onCheckedChange={(c) => setSendEmail(!!c)}
            />
            <span>Send confirmation email & PDF tickets to attendee immediately</span>
          </label>

          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setOpen(false)}
              className="text-xs h-8"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold text-xs h-8 gap-1.5"
            >
              {loading ? <RefreshCw size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
              <span>{loading ? "Activating..." : "Confirm & Issue Passes"}</span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
